/**
 * Client-side fingerprint generation + affiliate capture/claim.
 *
 * Ported from v1's `src/lib/fingerprint.ts`.
 *
 * Generates a browser fingerprint from environment signals and
 * communicates with the server's /affiliate/capture and /affiliate/claim endpoints.
 *
 * Uses the shared HTTP helper so native builds go through CapacitorHttp.
 */

import { publicRequest } from "@/shared/utils/api/apiClient"

const API_PREFIX = import.meta.env.VITE_API_BASE_URL ?? "/api"

export interface FingerprintPayload {
  ip: string
  userAgent: string
  screenResolution: string
  timezone: string
  timestamp: number
}

export interface TrackingData {
  affiliate?: string
  utm_source?: string
  utm_content?: string
  utm_medium?: string
  utm_term?: string
  utm_campaign?: string
  fbclid?: string
  gclid?: string
}

/**
 * Generate a fingerprint payload from browser environment.
 * The `ip` field is left empty — the server fills it from the request.
 */
export function generateFingerprint(): FingerprintPayload {
  return {
    ip: "",
    userAgent: navigator.userAgent || "",
    screenResolution: `${screen.width || 0}x${screen.height || 0}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    timestamp: Date.now(),
  }
}

/**
 * Capture tracking data with fingerprint.
 * Called when user lands on page with affiliate/UTM parameters.
 */
export async function captureAffiliate(
  trackingData: TrackingData,
): Promise<string | null> {
  try {
    const fingerprint = generateFingerprint()

    const res = await publicRequest<{ fingerprintId?: string }>({
      url: `${API_PREFIX}/affiliate/capture`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: { ...fingerprint, ...trackingData },
    })

    if (!res.ok) {
      console.warn("[Affiliate] Capture failed:", res.status)
      return null
    }

    return res.data?.fingerprintId ?? null
  } catch (error) {
    console.warn("[Affiliate] Capture error:", error)
    return null
  }
}

/**
 * Claim tracking data using fingerprint matching.
 * Called when user opens the app — matches against previously captured fingerprints.
 * Returns tracking data if a match is found, null otherwise.
 */
export async function claimAffiliate(): Promise<{
  trackingData: TrackingData
} | null> {
  try {
    const fingerprint = generateFingerprint()

    const res = await publicRequest<{ trackingData?: TrackingData }>({
      url: `${API_PREFIX}/affiliate/claim`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: { ...fingerprint },
    })

    if (res.status === 404) {
      // No match — expected in many cases
      return null
    }

    if (!res.ok) {
      console.warn("[Affiliate] Claim failed:", res.status)
      return null
    }

    return { trackingData: res.data?.trackingData || {} }
  } catch (error) {
    console.warn("[Affiliate] Claim error:", error)
    return null
  }
}
